// Quick Start — Feb 2026 · iter48.
//
// A guided 4-step "3 setup basics + Ready" onboarding page that gets a
// brand-new contractor from signup to their first estimate. Copy assumes
// the contractor has never used estimating software before.
//
// Steps:
//   1 of 3 · Company Basics   — PUT /api/company-profile (merge-safe)
//   2 of 3 · Pricing Basics   — PUT /api/pricing-profile + mark-ready
//   3 of 3 · Document Basics  — PUT /api/document-defaults + closeout-defaults
//   Ready                     — big CTA to /app/estimates/new
//
// Non-negotiable Slice 1b contract still applies (see iter41 header):
//   * ZERO new backend endpoints. Every save uses an existing endpoint.
//   * ZERO schema changes.
//   * ZERO pricing math touches.
//   * Full settings pages remain reachable. Quick Start is a curated
//     shortcut, never a replacement for /app/company-profile,
//     /app/pricing-profile, /app/setup/document-defaults,
//     /app/setup/closeout-defaults, or the legacy Pricing Profile Wizard.
//   * Presets are ALWAYS editable before save.
//
// iter48 additions:
//   * Bidirectional sync with Company Profile — Quick Start PUT is
//     merge-safe (fetches current profile first, only overrides the
//     fields Quick Start owns).
//   * Optional logo upload lives inside Company Basics.
//   * Time Zone dropdown with browser auto-detect.
//   * Progress wording: "Step X of 3 · Setup basics" then "Ready".
//   * Pricing Basics clarifies company-wide defaults + Margin vs Markup.
//   * Document Basics renamed "Default Document Wording" with placement
//     helper text under every field.
//   * Zero seeded placeholders that reference other test companies.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, API, explainSaveError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import {
  ArrowRight, ArrowLeft, Check, Rocket, Building2, Wallet, FileText,
  ShieldCheck, Sparkles, Loader2, Settings2, Upload, Trash2, ImagePlus,
  Info,
} from "lucide-react";
import {
  DOCUMENT_PRESETS, DOCUMENT_PRESET_ORDER, DOCUMENT_PRESET_DISCLAIMER,
  renderPreset,
} from "@/lib/documentPresets";
import {
  TIME_ZONE_OPTIONS, DEFAULT_TIME_ZONE, detectBrowserTimeZone,
} from "@/lib/timeZones";

// Setup basics are the 3 numbered steps; "Ready" is the completion screen.
const STEPS = [
  { id: 1, key: "company",   label: "Company Basics",  icon: Building2 },
  { id: 2, key: "pricing",   label: "Pricing Basics",  icon: Wallet },
  { id: 3, key: "documents", label: "Document Basics", icon: FileText },
  { id: 4, key: "ready",     label: "Ready",           icon: Rocket },
];
const SETUP_STEP_COUNT = 3; // 3 setup basics, then Ready

const LOGO_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const LOGO_MAX_MB = 2;

const hashToStep = (hash) => {
  const m = /^#step-([1-4])$/.exec(hash || "");
  return m ? Number(m[1]) : 1;
};
const stepToHash = (step) => `#step-${step}`;

const QuickStart = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(() => hashToStep(location.hash));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    // Step 1 · Company Basics
    company_name: "",
    primary_trade: "",
    trade_categories: [],
    phone: "",
    email: "",
    service_area_notes: "",
    time_zone: "",
    // Step 2 · Pricing Basics
    default_pricing_style: "hybrid",
    labor_rate: 65,
    markup_pct: 25,
    profit_margin_pct: 10,
    tax_rate: 8,
    trip_charge: 0,
    deposit_pct: 50,
    // Step 3 · Document Basics
    proposal_intro: "",
    contract_terms: "",
    invoice_notes: "",
    workmanship_warranty_terms: "",
    invoice_due_days: 15,
    warranty_years: 1,
  });
  const [tradeChoices, setTradeChoices] = useState([]);

  // Logo state — mirrored from GET /api/company-profile.
  const [hasLogo, setHasLogo] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [userIdForLogo, setUserIdForLogo] = useState(null);

  // Keep the URL hash and the ``step`` state in sync.
  useEffect(() => {
    const next = hashToStep(location.hash);
    if (next !== step) setStep(next);
  }, [location.hash]);

  const goStep = (n) => {
    const clamped = Math.max(1, Math.min(STEPS.length, n));
    setStep(clamped);
    navigate({ hash: stepToHash(clamped) }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Bootstrap: prefill from existing profiles + auto-detect time zone.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/company-profile").catch(() => null),
      api.get("/pricing-profile").catch(() => null),
      api.get("/document-defaults").catch(() => null),
      api.get("/closeout-defaults").catch(() => null),
      api.get("/company-profile/trade-choices").catch(() => null),
    ]).then(([cp, pp, dd, cd, tc]) => {
      if (cancelled) return;
      const cpData = cp?.data || {};
      const savedTz = cpData.time_zone || "";
      setForm((prev) => ({
        ...prev,
        ...(cp?.data
          ? {
              company_name: cpData.company_name || "",
              primary_trade: cpData.primary_trade || "",
              trade_categories: cpData.trade_categories || [],
              phone: cpData.phone || "",
              email: cpData.email || "",
              service_area_notes: cpData.service_area_notes || "",
              time_zone: savedTz || detectBrowserTimeZone(),
            }
          : { time_zone: detectBrowserTimeZone() }),
        ...(pp?.data
          ? {
              default_pricing_style: pp.data.default_pricing_style || "hybrid",
              labor_rate: pp.data.labor_rate ?? 65,
              markup_pct: pp.data.markup_pct ?? 25,
              profit_margin_pct: pp.data.profit_margin_pct ?? 10,
              tax_rate: pp.data.tax_rate ?? 8,
              trip_charge: pp.data.trip_charge ?? 0,
              deposit_pct: pp.data.deposit_pct ?? 50,
            }
          : {}),
        ...(dd?.data
          ? {
              proposal_intro: dd.data.proposal_intro || "",
              contract_terms: dd.data.contract_terms || "",
              invoice_notes: dd.data.invoice_notes || "",
            }
          : {}),
        ...(cd?.data
          ? {
              workmanship_warranty_terms: cd.data.workmanship_warranty_terms || "",
            }
          : {}),
      }));
      setTradeChoices(Array.isArray(tc?.data) ? tc.data : (tc?.data?.choices || []));
      setHasLogo(!!cpData.has_logo);
      setUserIdForLogo(cpData.user_id || null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the actual logo bytes when a logo exists (auth-guarded endpoint).
  useEffect(() => {
    let revokeUrl = null;
    if (hasLogo && userIdForLogo) {
      const token = localStorage.getItem("jc_token");
      fetch(`${API}/company-profile/logo/${userIdForLogo}?t=${logoBust}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => (res.ok ? res.blob() : null))
        .then((blob) => {
          if (!blob) return setLogoBlobUrl(null);
          const url = URL.createObjectURL(blob);
          revokeUrl = url;
          setLogoBlobUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
          });
        })
        .catch(() => setLogoBlobUrl(null));
    } else {
      setLogoBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
    return () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [hasLogo, logoBust, userIdForLogo]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const toggleTrade = (label) => {
    setForm((prev) => {
      const has = prev.trade_categories.includes(label);
      return {
        ...prev,
        trade_categories: has
          ? prev.trade_categories.filter((t) => t !== label)
          : [...prev.trade_categories, label],
      };
    });
  };

  const monogram = useMemo(() => {
    return (
      (form.company_name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("") || "?"
    );
  }, [form.company_name]);

  // ---- Save handlers per step. All use existing endpoints. ----

  const saveCompany = async () => {
    setBusy(true);
    try {
      // iter48 · MERGE-SAFE PUT — fetch the current profile so fields owned
      // by Company Profile (address, license, DBA, brand colors, etc.) are
      // preserved. Quick Start only overrides the fields it presents.
      const current = await api.get("/company-profile").catch(() => ({ data: {} }));
      const cur = current.data || {};
      const payload = {
        // Preserve everything Company Profile owns:
        dba: cur.dba || "",
        website: cur.website || "",
        address: cur.address || { line1: "", line2: "", city: "", state: "", zip: "" },
        license_number: cur.license_number || "",
        license_state: cur.license_state || "",
        license_expires: cur.license_expires || "",
        brand_primary_hex: cur.brand_primary_hex || "",
        brand_accent_hex: cur.brand_accent_hex || "",
        // Quick Start owns these:
        company_name: form.company_name,
        primary_trade: form.primary_trade,
        trade_categories: form.trade_categories,
        phone: form.phone,
        email: form.email,
        service_area_notes: form.service_area_notes,
        time_zone: form.time_zone || DEFAULT_TIME_ZONE,
      };
      await api.put("/company-profile", payload);
      toast.success(t("onboarding.saved"));
      goStep(2);
    } catch (err) {
      toast.error(explainSaveError(err, "company basics"));
    } finally {
      setBusy(false);
    }
  };

  const savePricing = async () => {
    setBusy(true);
    try {
      const currentProfile = await api.get("/pricing-profile");
      const payload = {
        ...currentProfile.data,
        default_pricing_style: form.default_pricing_style,
        labor_rate: Number(form.labor_rate) || 0,
        markup_pct: Number(form.markup_pct) || 0,
        profit_margin_pct: Number(form.profit_margin_pct) || 0,
        tax_rate: Number(form.tax_rate) || 0,
        trip_charge: Number(form.trip_charge) || 0,
        deposit_pct: Number(form.deposit_pct) || 0,
      };
      delete payload.id;
      delete payload.user_id;
      delete payload.updated_at;
      await api.put("/pricing-profile", payload);
      // iter54 correction · Quick Start basics don't ship per-trade
      // material_rules, so the shared readiness evaluator will refuse
      // mark-ready with HTTP 400 (pricing_incomplete). That's expected
      // here — the contractor completes real pricing later under
      // Pricing Profile. Swallow the 400 so the wizard advances; any
      // other error still surfaces to the user.
      try {
        await api.post("/pricing-profile/mark-ready");
      } catch (mrErr) {
        if (mrErr?.response?.status !== 400) throw mrErr;
      }
      toast.success(t("onboarding.saved"));
      goStep(3);
    } catch (err) {
      toast.error(explainSaveError(err, "pricing basics"));
    } finally {
      setBusy(false);
    }
  };

  const saveDocuments = async () => {
    setBusy(true);
    try {
      const existingDD = await api.get("/document-defaults").catch(() => ({ data: {} }));
      const ddPayload = {
        ...existingDD.data,
        proposal_intro: form.proposal_intro,
        contract_terms: form.contract_terms,
        invoice_notes: form.invoice_notes,
      };
      delete ddPayload.id;
      delete ddPayload.user_id;
      delete ddPayload.updated_at;
      await api.put("/document-defaults", ddPayload);

      const existingCD = await api.get("/closeout-defaults").catch(() => ({ data: {} }));
      const cdPayload = {
        ...existingCD.data,
        workmanship_warranty_terms: form.workmanship_warranty_terms,
      };
      delete cdPayload.id;
      delete cdPayload.user_id;
      delete cdPayload.updated_at;
      await api.put("/closeout-defaults", cdPayload);

      toast.success(t("onboarding.saved"));
      goStep(4);
    } catch (err) {
      toast.error(explainSaveError(err, "document basics"));
    } finally {
      setBusy(false);
    }
  };

  const applyPreset = (fieldKey) => {
    const body = renderPreset(fieldKey, {
      companyName: form.company_name,
      invoiceDueDays: form.invoice_due_days,
      warrantyYears: form.warranty_years,
    });
    if (!body) {
      toast.error(
        "Add your company name in Company Basics before inserting starter copy — the template needs it to fill in your business name.",
        { duration: 6000 },
      );
      return;
    }
    setField(fieldKey, body);
    toast.message("Starter copy filled with your company name — edit to match your business.");
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > LOGO_MAX_MB * 1024 * 1024) {
      toast.error(`Logo exceeds ${LOGO_MAX_MB} MB limit`);
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company-profile/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHasLogo(!!data.has_logo);
      setUserIdForLogo(data.user_id || userIdForLogo);
      setLogoBust(Date.now());
      toast.success("Logo uploaded — you can replace or remove it any time.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the logo you just uploaded?")) return;
    try {
      const { data } = await api.delete("/company-profile/logo");
      setHasLogo(!!data.has_logo);
      setLogoBust(Date.now());
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const renderStepNav = () => (
    <div
      className="mt-8 flex items-center justify-between gap-3"
      data-testid="quick-start-nav"
    >
      <button
        type="button"
        onClick={() => goStep(step - 1)}
        disabled={step === 1 || busy}
        data-testid="quick-start-back-btn"
        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-40"
      >
        <ArrowLeft size={14} /> Back
      </button>
      {step === 1 && (
        <button
          onClick={saveCompany}
          disabled={busy || !form.company_name.trim()}
          data-testid="quick-start-save-company-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : null}
          Save &amp; Continue <ArrowRight size={14} />
        </button>
      )}
      {step === 2 && (
        <button
          onClick={savePricing}
          disabled={busy}
          data-testid="quick-start-save-pricing-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : null}
          Save &amp; Continue <ArrowRight size={14} />
        </button>
      )}
      {step === 3 && (
        <button
          onClick={saveDocuments}
          disabled={busy}
          data-testid="quick-start-save-documents-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : null}
          Save &amp; Continue <ArrowRight size={14} />
        </button>
      )}
    </div>
  );

  // iter48 · progress label reflects "3 setup basics then Ready".
  const progressLabel =
    step <= SETUP_STEP_COUNT
      ? `Step ${step} of ${SETUP_STEP_COUNT} · Setup basics`
      : "Basics saved";

  return (
    <DashboardLayout
      title={t("onboarding.step_business_trades")}
      subtitle={t("onboarding.business_trades_body")}
      actions={
        <Link
          to="/app"
          data-testid="quick-start-skip-link"
          className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] inline-flex items-center gap-1"
        >
          Skip for now <ArrowRight size={12} />
        </Link>
      }
    >
      <div className="max-w-4xl" data-testid="quick-start-page">
        {/* Progress label + Stepper */}
        <div className="mb-3 flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full bg-[var(--jc-navy)]/10 text-[var(--jc-navy)]"
            data-testid="quick-start-progress-label"
          >
            {progressLabel}
          </span>
        </div>
        <div className="flex items-center gap-2 mb-8 overflow-x-auto" data-testid="quick-start-stepper">
          {STEPS.map((s, i) => {
            const active = s.id === step;
            const done = s.id < step;
            return (
              <div
                key={s.key}
                data-testid={`quick-start-step-pill-${s.id}`}
                className="flex items-center gap-2 shrink-0"
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    done
                      ? "bg-emerald-600 text-white"
                      : active
                      ? "bg-[var(--jc-navy)] text-white"
                      : "bg-[var(--jc-border)] text-[var(--jc-text-soft)]"
                  }`}
                >
                  {done ? <Check size={14} /> : (s.id <= SETUP_STEP_COUNT ? s.id : <Rocket size={12} />)}
                </div>
                <div className={`text-xs font-semibold ${active ? "text-[var(--jc-text)]" : "text-[var(--jc-text-soft)]"}`}>
                  {s.label}
                </div>
                {i < STEPS.length - 1 && <div className="w-6 h-px bg-[var(--jc-border)] mx-1" />}
              </div>
            );
          })}
        </div>

        <div className="jc-card p-6 lg:p-8">
          {loading ? (
            <div className="text-sm text-[var(--jc-text-soft)]">{t("onboarding.loading")}</div>
          ) : (
            <>
              {step === 1 && (
                <div className="space-y-5" data-testid="quick-start-step-company">
                  <div className="overline">{t("onboarding.quickstart_step1_overline")}</div>
                  <h2 className="font-display text-2xl">{t("onboarding.quickstart_step1_headline")}</h2>
                  <p className="text-sm text-[var(--jc-text-soft)]">
                    This is what shows up on customer-facing proposals, contracts, and invoices.
                    Everything here saves straight to your{" "}
                    <Link to="/app/company-profile" className="text-[var(--jc-blue)] font-semibold" data-testid="qs-open-company-profile">
                      Company Profile
                    </Link>{" "}
                    — you can polish the rest (address, license, brand colors) any time.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Company name *" hint="Shown on every customer document.">
                      <input
                        data-testid="qs-company-name-input"
                        className="jc-input"
                        value={form.company_name}
                        onChange={(e) => setField("company_name", e.target.value)}
                        placeholder="Your company"
                      />
                    </Field>
                    <Field label="Primary trade / service" hint="One-line summary of the work you do most.">
                      <input
                        data-testid="qs-primary-trade-input"
                        className="jc-input"
                        value={form.primary_trade}
                        onChange={(e) => setField("primary_trade", e.target.value)}
                        placeholder="e.g. Roofing, Flooring, General Contractor"
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        data-testid="qs-phone-input"
                        className="jc-input"
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        placeholder="(555) 555-5555"
                      />
                    </Field>
                    <Field label="Contact email">
                      <input
                        data-testid="qs-email-input"
                        type="email"
                        className="jc-input"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        placeholder="office@company.com"
                      />
                    </Field>
                    <Field
                      label="Time zone"
                      hint="Auto-detected from your browser. Change this if you crew from a different region."
                    >
                      <select
                        data-testid="qs-timezone-select"
                        className="jc-input"
                        value={form.time_zone || DEFAULT_TIME_ZONE}
                        onChange={(e) => setField("time_zone", e.target.value)}
                      >
                        {TIME_ZONE_OPTIONS.map((tz) => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Service area (optional)">
                      <input
                        data-testid="qs-service-area-input"
                        className="jc-input"
                        value={form.service_area_notes}
                        onChange={(e) => setField("service_area_notes", e.target.value)}
                        placeholder="e.g. San Antonio and surrounding 50 mi"
                      />
                    </Field>
                  </div>
                  {tradeChoices.length > 0 && (
                    <div className="pt-2" data-testid="qs-trade-categories-block">
                      <div className="text-xs font-semibold text-[var(--jc-text-soft)] mb-2">
                        Trades you cover (optional — tap any that apply)
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {tradeChoices.map((label) => {
                          const active = form.trade_categories.includes(label);
                          return (
                            <button
                              key={label}
                              type="button"
                              data-testid={`qs-trade-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              onClick={() => toggleTrade(label)}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                                active
                                  ? "bg-[var(--jc-navy)] text-white border-[var(--jc-navy)]"
                                  : "border-[var(--jc-border)] text-[var(--jc-text)] hover:bg-[var(--jc-surface-hover)]"
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* iter48 · Optional logo upload lives inside Company Basics.
                      It's skippable — no logo, no block. */}
                  <div className="pt-4 border-t border-[var(--jc-border)]" data-testid="qs-logo-block">
                    <div className="flex items-center gap-2 mb-2">
                      <ImagePlus size={14} className="text-[var(--jc-text-soft)]" />
                      <div className="overline">{t("onboarding.quickstart_company_logo_optional")}</div>
                    </div>
                    <p className="text-xs text-[var(--jc-text-soft)] mb-3">
                      Adds your logo to customer proposals, contracts, and invoices. You can skip this
                      and come back to it later — the JobClose AI logo stays in the app either way.
                    </p>
                    <div className="flex items-center gap-4">
                      <div
                        className="w-24 h-24 rounded-md border border-dashed border-[var(--jc-border)] bg-[var(--jc-surface)] flex items-center justify-center overflow-hidden shrink-0"
                        data-testid="qs-logo-preview"
                      >
                        {hasLogo && logoBlobUrl ? (
                          <img
                            src={logoBlobUrl}
                            alt="Company logo"
                            className="max-h-full max-w-full object-contain"
                            data-testid="qs-logo-img"
                          />
                        ) : (
                          <div
                            className="w-14 h-14 rounded-md bg-[var(--jc-navy)] text-white flex items-center justify-center font-display text-lg"
                            data-testid="qs-logo-monogram"
                          >
                            {monogram}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={uploadingLogo}
                            data-testid="qs-logo-upload-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                          >
                            {uploadingLogo ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />}
                            {hasLogo ? "Replace logo" : "Upload logo"}
                          </button>
                          {hasLogo && (
                            <button
                              type="button"
                              onClick={removeLogo}
                              data-testid="qs-logo-remove-btn"
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-[var(--jc-border)] text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={12} /> Remove
                            </button>
                          )}
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept={LOGO_ACCEPT}
                            className="hidden"
                            data-testid="qs-logo-input"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadLogo(f);
                              e.target.value = "";
                            }}
                          />
                        </div>
                        <p className="text-[11px] text-[var(--jc-text-soft)]">
                          PNG · JPG · WEBP · SVG · up to {LOGO_MAX_MB} MB · Optional — skip any time.
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-[var(--jc-text-soft)] pt-2">
                    Address, license, DBA, and brand colors live in the full{" "}
                    <Link to="/app/company-profile" className="text-[var(--jc-blue)] font-semibold" data-testid="qs-branding-link">
                      Company Profile
                    </Link>{" "}
                    — nothing you enter here is lost when you open that page.
                  </p>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5" data-testid="quick-start-step-pricing">
                  <div className="overline">Step 2 of {SETUP_STEP_COUNT} · Pricing Basics</div>
                  <h2 className="font-display text-2xl">Set your company-wide pricing defaults.</h2>
                  <p className="text-sm text-[var(--jc-text-soft)]">
                    These are your <b>company-wide starting defaults</b> — the numbers we&apos;ll use
                    on every new estimate until you say otherwise. You can override any of them per
                    job, per trade, or per line item. New to this? The values below are safe starting
                    points; adjust to match how you actually price work today.
                  </p>
                  <div
                    className="jc-card p-3 bg-[var(--jc-blue)]/5 border-l-4 border-l-[var(--jc-blue)] flex items-start gap-2"
                    data-testid="qs-pricing-explainer"
                  >
                    <Info size={14} className="text-[var(--jc-blue)] mt-0.5 shrink-0" />
                    <div className="text-xs text-[var(--jc-text)] leading-relaxed">
                      <b>Markup vs. profit margin — the quick version:</b>
                      <ul className="list-disc pl-4 mt-1 space-y-0.5">
                        <li><b>Markup %</b> is added on top of your cost (cost × markup). Common examples: 20–35%.</li>
                        <li><b>Profit margin %</b> is the profit you want left after overhead — a target, not a multiplier.</li>
                        <li>Per-trade rules and material price books live in the full{" "}
                          <Link to="/app/pricing-profile" className="text-[var(--jc-blue)] font-semibold" data-testid="qs-full-pricing-link">
                            Pricing Profile
                          </Link>.
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Default pricing style" hint="How you usually present numbers to customers.">
                      <select
                        data-testid="qs-pricing-style-select"
                        className="jc-input"
                        value={form.default_pricing_style}
                        onChange={(e) => setField("default_pricing_style", e.target.value)}
                      >
                        <option value="unit">Unit / Production Pricing</option>
                        <option value="flat">Flat / Lump Sum</option>
                        <option value="labor_materials">Labor + Materials</option>
                        <option value="hybrid">Hybrid / Depends on Job</option>
                      </select>
                    </Field>
                    <Field label="Labor rate ($/hr)" hint="Blended crew rate. Change per-trade later.">
                      <input
                        type="number"
                        data-testid="qs-labor-rate-input"
                        className="jc-input font-mono-jc"
                        value={form.labor_rate}
                        onChange={(e) => setField("labor_rate", e.target.value)}
                      />
                    </Field>
                    <Field label="Markup (%)" hint="Added on top of your cost. Example: 25% markup on $100 cost = $125.">
                      <input
                        type="number"
                        data-testid="qs-markup-input"
                        className="jc-input font-mono-jc"
                        value={form.markup_pct}
                        onChange={(e) => setField("markup_pct", e.target.value)}
                      />
                    </Field>
                    <Field label="Overhead / profit margin (%)" hint="Profit target left after overhead — a goal, not a formula.">
                      <input
                        type="number"
                        data-testid="qs-overhead-input"
                        className="jc-input font-mono-jc"
                        value={form.profit_margin_pct}
                        onChange={(e) => setField("profit_margin_pct", e.target.value)}
                      />
                    </Field>
                    <Field label="Sales tax (%)" hint="Applied to taxable line items on customer estimates.">
                      <input
                        type="number"
                        data-testid="qs-tax-input"
                        className="jc-input font-mono-jc"
                        value={form.tax_rate}
                        onChange={(e) => setField("tax_rate", e.target.value)}
                      />
                    </Field>
                    <Field label="Trip charge ($)" hint="Flat show-up fee — leave 0 if you don't use one.">
                      <input
                        type="number"
                        data-testid="qs-trip-input"
                        className="jc-input font-mono-jc"
                        value={form.trip_charge}
                        onChange={(e) => setField("trip_charge", e.target.value)}
                      />
                    </Field>
                    <Field label="Default deposit (%)" hint="Percentage requested up-front on approved jobs.">
                      <input
                        type="number"
                        data-testid="qs-deposit-input"
                        className="jc-input font-mono-jc"
                        value={form.deposit_pct}
                        onChange={(e) => setField("deposit_pct", e.target.value)}
                      />
                    </Field>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5" data-testid="quick-start-step-documents">
                  <div className="overline">Step 3 of {SETUP_STEP_COUNT} · Document Basics</div>
                  <h2 className="font-display text-2xl" data-testid="qs-documents-headline">
                    Default Document Wording
                  </h2>
                  <p className="text-sm text-[var(--jc-text-soft)]">
                    Write the paragraphs your customer reads on every proposal, contract, invoice,
                    and warranty. We&apos;ll drop them in automatically so you never have to re-type
                    the same boilerplate. You can still edit them per-job.
                  </p>
                  <p
                    className="text-xs text-[var(--jc-text-soft)] leading-relaxed"
                    data-testid="qs-preset-disclaimer"
                  >
                    {DOCUMENT_PRESET_DISCLAIMER}
                  </p>

                  {!form.company_name?.trim() && (
                    <div
                      data-testid="qs-missing-company-warning"
                      className="jc-card p-3 border-l-4 border-l-yellow-400 bg-yellow-50/50 flex items-start gap-2"
                    >
                      <Sparkles size={14} className="text-yellow-700 mt-0.5 shrink-0" />
                      <div className="text-xs text-yellow-900">
                        <b>Add your company name first.</b> Starter copy fills in your business
                        name automatically — head back to{" "}
                        <button
                          type="button"
                          onClick={() => goStep(1)}
                          className="underline font-semibold"
                          data-testid="qs-missing-company-goto-step1"
                        >
                          Step 1 · Company Basics
                        </button>{" "}
                        to enter it before inserting starter copy.
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <label className="flex flex-col gap-1.5" data-testid="qs-invoice-due-days-block">
                      <span className="text-xs font-semibold text-[var(--jc-text-soft)]">
                        Invoice payment due within (days)
                      </span>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        data-testid="qs-invoice-due-days-input"
                        className="jc-input font-mono-jc"
                        value={form.invoice_due_days}
                        onChange={(e) => setField("invoice_due_days", e.target.value)}
                      />
                      <span className="text-[11px] text-[var(--jc-text-soft)]">
                        Filled into your starter invoice notes below.
                      </span>
                    </label>
                    <label className="flex flex-col gap-1.5" data-testid="qs-warranty-years-block">
                      <span className="text-xs font-semibold text-[var(--jc-text-soft)]">
                        Workmanship warranty length (years)
                      </span>
                      <input
                        type="number"
                        min="1"
                        max="99"
                        data-testid="qs-warranty-years-input"
                        className="jc-input font-mono-jc"
                        value={form.warranty_years}
                        onChange={(e) => setField("warranty_years", e.target.value)}
                      />
                      <span className="text-[11px] text-[var(--jc-text-soft)]">
                        Filled into your starter workmanship warranty below.
                      </span>
                    </label>
                  </div>

                  {DOCUMENT_PRESET_ORDER.map((key) => {
                    const preset = DOCUMENT_PRESETS[key];
                    const canApply = !!form.company_name?.trim();
                    const placement = DOCUMENT_PLACEMENT_HINT[key];
                    return (
                      <div key={key} data-testid={`qs-doc-block-${key}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">
                            {preset.label.replace(/^Starter /, "")}
                          </label>
                          <button
                            type="button"
                            data-testid={`qs-use-preset-btn-${key}`}
                            onClick={() => applyPreset(key)}
                            disabled={!canApply}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border ${
                              canApply
                                ? "border-[var(--jc-border)] text-[var(--jc-blue)] hover:bg-[var(--jc-blue)]/5"
                                : "border-[var(--jc-border)] text-[var(--jc-text-soft)] opacity-50 cursor-not-allowed"
                            }`}
                            title={canApply ? "Fill starter copy with your company name" : "Add company name in Step 1 first"}
                          >
                            <Sparkles size={11} /> Use starter copy
                          </button>
                        </div>
                        <p
                          className="text-[11px] text-[var(--jc-text-soft)] mb-1.5"
                          data-testid={`qs-doc-placement-${key}`}
                        >
                          <b className="text-[var(--jc-text)]">Where this shows up:</b> {placement}
                        </p>
                        <textarea
                          data-testid={`qs-doc-field-${key}`}
                          rows={4}
                          className="jc-input font-mono text-sm"
                          value={form[key]}
                          onChange={(e) => setField(key, e.target.value)}
                          placeholder="Leave blank to use JobClose defaults, or click Use starter copy to fill and edit."
                        />
                        <p
                          className="text-[11px] text-[var(--jc-text-soft)] mt-1"
                          data-testid={`qs-doc-hint-${key}`}
                        >
                          {preset.editorHint}
                        </p>
                      </div>
                    );
                  })}

                  <p className="text-[11px] text-[var(--jc-text-soft)] pt-1">
                    Every field is optional. Full document + closeout options (manufacturer warranty,
                    maintenance, review requests, referral asks) live in{" "}
                    <Link to="/app/setup/document-defaults" className="text-[var(--jc-blue)] font-semibold" data-testid="qs-full-docs-link">
                      Document Defaults
                    </Link>{" "}
                    and{" "}
                    <Link to="/app/setup/closeout-defaults" className="text-[var(--jc-blue)] font-semibold" data-testid="qs-full-closeout-link">
                      Closeout Defaults
                    </Link>{" "}
                    under Advanced Setup.
                  </p>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4" data-testid="quick-start-step-ready">
                  <div className="overline">Setup basics · Saved</div>
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-14 h-14 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
                      <ShieldCheck size={24} />
                    </div>
                    <div>
                      <h2 className="font-display text-2xl" data-testid="qs-ready-headline">
                        Quick Start basics are saved.
                      </h2>
                      <p className="text-sm text-[var(--jc-text-soft)] mt-2 max-w-2xl">
                        You can start your first estimate now. To let JobClose AI auto-price future
                        scopes end-to-end, finish your saved trade pricing under Advanced Setup —
                        we&apos;ll warn you inside the estimate editor if a job still needs prices.
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 pt-4">
                    <Link
                      to="/app/estimates/new"
                      data-testid="qs-ready-create-first-job-btn"
                      className="jc-card p-5 flex items-center justify-between gap-3 hover:border-[var(--jc-blue)] transition"
                    >
                      <div>
                        <div className="overline text-[var(--jc-blue)]">Recommended</div>
                        <div className="font-display text-lg mt-1">Create your first job</div>
                        <div className="text-xs text-[var(--jc-text-soft)] mt-1">
                          Start a new estimate — voice, notes, and AI scope from here.
                        </div>
                      </div>
                      <ArrowRight className="text-[var(--jc-blue)]" size={18} />
                    </Link>
                    <Link
                      to="/app"
                      data-testid="qs-ready-dashboard-btn"
                      className="jc-card p-5 flex items-center justify-between gap-3 hover:border-[var(--jc-navy)] transition"
                    >
                      <div>
                        <div className="overline">Or</div>
                        <div className="font-display text-lg mt-1">Go to Dashboard</div>
                        <div className="text-xs text-[var(--jc-text-soft)] mt-1">
                          Review your setup summary and finish Advanced Setup later.
                        </div>
                      </div>
                      <ArrowRight size={18} />
                    </Link>
                  </div>

                  <div className="pt-4 border-t border-[var(--jc-border)] mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Settings2 size={14} className="text-[var(--jc-text-soft)]" />
                      <div className="overline">Advanced Setup — finish later</div>
                    </div>
                    <ul className="text-xs text-[var(--jc-text-soft)] grid sm:grid-cols-2 gap-1.5" data-testid="qs-advanced-links">
                      <li>
                        <Link className="text-[var(--jc-blue)] font-semibold" to="/app/company-profile" data-testid="qs-adv-link-company">
                          Full Company Profile &amp; branding
                        </Link>
                      </li>
                      <li>
                        <Link className="text-[var(--jc-blue)] font-semibold" to="/app/pricing-profile" data-testid="qs-adv-link-pricing">
                          Advanced Pricing Profile / trade templates
                        </Link>
                      </li>
                      <li>
                        <Link className="text-[var(--jc-blue)] font-semibold" to="/app/pricing-profile/materials" data-testid="qs-adv-link-materials">
                          Materials Price Book
                        </Link>
                      </li>
                      <li>
                        <Link className="text-[var(--jc-blue)] font-semibold" to="/app/setup/document-defaults" data-testid="qs-adv-link-docs">
                          Detailed Document Defaults
                        </Link>
                      </li>
                      <li>
                        <Link className="text-[var(--jc-blue)] font-semibold" to="/app/setup/closeout-defaults" data-testid="qs-adv-link-closeout">
                          Detailed Closeout / Warranty Defaults
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {renderStepNav()}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// iter48 · One-line "where this shows up" hint per preset key. Rendered
// above the textarea so the contractor sees the customer-facing placement
// before they touch the words.
const DOCUMENT_PLACEMENT_HINT = {
  proposal_intro:
    "Top of every customer proposal, right under your logo.",
  contract_terms:
    "Body of your customer contract, under the scope of work section.",
  invoice_notes:
    "Footer of every invoice, under the amount due.",
  workmanship_warranty_terms:
    "Warranty section of your closeout packet, delivered when a job is complete.",
};

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{label}</span>
    {children}
    {hint && <span className="text-[11px] text-[var(--jc-text-soft)]">{hint}</span>}
  </label>
);

export default QuickStart;
